import { t } from '@lingui/core/macro';
import { AutoRenewalStatus } from '../enums';
import { Subscription } from '../types/Subscription.types';

/**
 * Checks if a subscription is expiring (INACTIVE with validUntil in the future)
 */
export const isSubscriptionExpiring = (subscription: Subscription): boolean => {
  if (subscription.autoRenewal !== AutoRenewalStatus.INACTIVE || !subscription.validUntil) {
    return false;
  }
  const validUntilDate = new Date(subscription.validUntil);
  const now = new Date();
  return validUntilDate > now;
};

/**
 * Checks if a subscription is active or expiring
 */
export const isSubscriptionActiveOrExpiring = (subscription: Subscription): boolean => {
  if (subscription.autoRenewal === AutoRenewalStatus.ACTIVE) {
    return true;
  }
  return isSubscriptionExpiring(subscription);
};

/**
 * Determines the status badge for a subscription
 * A subscription is "expiring" only if autoRenewal is INACTIVE AND validUntil is in the future
 */
export const getSubscriptionBadge = (subscription: Subscription): string | null => {
  // Check if subscription has user data (boughtAt means it's owned by user)
  if (subscription.boughtAt) {
    if (subscription.autoRenewal === AutoRenewalStatus.ACTIVE) {
      return 'active';
    }
    if (isSubscriptionExpiring(subscription)) {
      return 'expiring';
    }
    return null;
  }
  if (subscription.highlight) {
    return 'popular';
  }
  return null;
};

/**
 * Determines the CTA button text based on subscription status
 */
export const getSubscriptionCTA = (subscription: Subscription): string => {
  // If subscription has boughtAt, it's owned by the user
  if (subscription.boughtAt) {
    return 'manage';
  }
  return 'select';
};

/**
 * Merges available subscriptions with user's owned subscriptions
 */
export const mergeSubscriptions = (
  availableSubscriptions: Subscription[],
  mySubscriptions: Subscription[],
): Subscription[] => {
  const availableSubscriptionsMap = new Map(availableSubscriptions.map((sub) => [sub.code, sub]));
  const mySubscriptionsMap = new Map(mySubscriptions.map((sub) => [sub.code, sub]));

  const enrichedAvailableSubscriptions = availableSubscriptions.map((subscription) => {
    const userSubscription = mySubscriptionsMap.get(subscription.code);
    if (userSubscription) {
      return {
        ...subscription,
        ...userSubscription,
        details: userSubscription.details || subscription.details,
      };
    }
    return subscription;
  });

  const userOnlySubscriptions = mySubscriptions.filter(
    (userSub) => !availableSubscriptionsMap.has(userSub.code),
  );

  return [...enrichedAvailableSubscriptions, ...userOnlySubscriptions];
};

/**
 * Checks if user has an active or expiring subscription
 */
export const hasActiveOrExpiringSubscription = (subscriptions: Subscription[]): boolean => {
  return subscriptions.some((sub) => isSubscriptionActiveOrExpiring(sub));
};

/**
 * Checks if subscription can be purchased (no active subscription exists)
 */
export const canPurchaseSubscription = (subscriptions: Subscription[]): boolean => {
  return !subscriptions.some((sub) => sub.boughtAt && sub.autoRenewal === AutoRenewalStatus.ACTIVE);
};

/**
 * Returns the billing cycle display label
 */
export const getBillingCycleLabel = (interval: string | undefined): string => {
  if (!interval) return '';

  switch (interval.toUpperCase()) {
    case 'DAILY':
      return t({ id: 'subscriptions.billingCycle.day', message: '/day' });
    case 'WEEKLY':
      return t({ id: 'subscriptions.billingCycle.week', message: '/week' });
    case 'MONTHLY':
      return t({ id: 'subscriptions.billingCycle.month', message: '/month' });
    case 'YEARLY':
      return t({ id: 'subscriptions.billingCycle.year', message: '/year' });
    case 'FIRST_OF_MONTH':
      return t({
        id: 'subscriptions.billingCycle.firstOfMonth',
        message: '/first of month',
      });
    case 'FIRST_OF_YEAR':
      return t({
        id: 'subscriptions.billingCycle.firstOfYear',
        message: '/first of year',
      });
    default:
      return `/${interval.toLowerCase()}`;
  }
};

/**
 * Returns the renewal period label for disclaimer text (e.g. "day", "month", "year")
 */
export const getRenewalPeriodLabel = (interval: string | undefined): string => {
  if (!interval) return '';

  switch (interval.toUpperCase()) {
    case 'DAILY':
      return t({ id: 'subscriptions.renewalPeriod.day', message: 'day' });
    case 'WEEKLY':
      return t({ id: 'subscriptions.renewalPeriod.week', message: 'week' });
    case 'MONTHLY':
      return t({ id: 'subscriptions.renewalPeriod.month', message: 'month' });
    case 'YEARLY':
      return t({ id: 'subscriptions.renewalPeriod.year', message: 'year' });
    case 'FIRST_OF_MONTH':
      return t({
        id: 'subscriptions.renewalPeriod.firstOfMonth',
        message: 'first of month',
      });
    case 'FIRST_OF_YEAR':
      return t({
        id: 'subscriptions.renewalPeriod.firstOfYear',
        message: 'first of year',
      });
    default:
      return interval.toLowerCase();
  }
};
